import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useFeedback } from '../../contexts/FeedbackContext';
import { getScreenLabelKey } from '../../utils/screenLabels';
import FeedbackCommentCard from './FeedbackCommentCard';

const PANEL_WIDTH = 380; // 360–420px per spec

function FeedbackPanel() {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    comments,
    loading,
    error,
    panelOpen,
    filter,
    setFilter,
    addComment,
    closePanel,
  } = useFeedback();
  const [inputValue, setInputValue] = useState('');
  const [savedNotice, setSavedNotice] = useState(false);

  const filteredComments = useMemo(() => {
    if (filter === 'all') return [...comments];
    const currentPath = location.pathname;
    const currentSegments = currentPath.split('/').filter(Boolean);
    const currentBase = currentSegments[0] || '';
    return comments.filter((c) => {
      if (c.screenPath === currentPath) return true;
      const commentSegments = c.screenPath.split('/').filter(Boolean);
      const commentBase = commentSegments[0] || '';
      return currentBase === commentBase;
    });
  }, [comments, filter, location.pathname]);

  const sortedComments = useMemo(
    () => [...filteredComments].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
    [filteredComments]
  );

  const handleAdd = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    const screenPath = location.pathname || '/';
    const labelKey = getScreenLabelKey(screenPath);
    const screenLabel = t(labelKey);
    const saved = await addComment(trimmed, screenPath, screenLabel);
    if (saved) {
      setInputValue('');
      setSavedNotice(true);
      window.setTimeout(() => setSavedNotice(false), 2200);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={panelOpen}
      onClose={closePanel}
      variant="temporary"
      ModalProps={{
        slotProps: {
          backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.2)' } },
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: PANEL_WIDTH },
          maxWidth: '100%',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          transition: 'transform 300ms ease-in-out',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.95) 100%)',
          backdropFilter: 'saturate(1.02)',
        },
      }}
      slotProps={{
        backdrop: { transitionDuration: 300 },
      }}
      transitionDuration={300}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {t('feedback.title')}
          </Typography>
          <IconButton onClick={closePanel} size="small" aria-label={t('common.close')}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Filter */}
        <Box sx={{ px: 2, pt: 1, flexShrink: 0 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>{t('feedback.filterLabel')}</InputLabel>
            <Select
              value={filter}
              label={t('feedback.filterLabel')}
              onChange={(e) => setFilter(e.target.value)}
              sx={{ borderRadius: '8px' }}
            >
              <MenuItem value="all">{t('feedback.filterAll')}</MenuItem>
              <MenuItem value="current">{t('feedback.filterCurrent')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Comment list */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          {error && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          {loading ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {t('common.loading')}
            </Typography>
          ) : sortedComments.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {t('feedback.empty')}
            </Typography>
          ) : (
            sortedComments.map((comment) => (
              <FeedbackCommentCard key={comment.id} comment={comment} />
            ))
          )}
        </Box>

        {/* Add comment footer */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            placeholder={t('feedback.addPlaceholder')}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="outlined"
            size="small"
            inputProps={{ maxLength: 2000 }}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
              },
            }}
          />
          <Button
            variant="contained"
            fullWidth
            onClick={handleAdd}
            disabled={!inputValue.trim()}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
          >
            {t('feedback.addButton')}
          </Button>
          {savedNotice && (
            <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1, textAlign: 'center', fontWeight: 600 }}>
              {t('feedback.saved')}
            </Typography>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

export default FeedbackPanel;
